/**
 * Product page: gallery dots, lightbox, size buttons, size guide, add to bag.
 *
 * Add to bag posts the form exactly as the browser would (so every plugin field,
 * e.g. Extra Product Options and Gift Cards, is handled by WooCommerce's normal
 * add-to-cart handler), reads any error notice from the response, then refreshes
 * the cart fragments and opens the bag drawer. If anything fails, the form submits
 * normally.
 */
const TC = window.TC || {};
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const endpoint = (n) => (TC.wcAjax || '/?wc-ajax=%%endpoint%%').replace('%%endpoint%%', n);

// ---------------------------------------------------------------------------
// Gallery: dots on phones, lightbox everywhere.
// ---------------------------------------------------------------------------
function initGallery() {
  const gal = $('[data-gal]');
  if (!gal) return;
  const dots = $$('.gal__dots i');
  if (dots.length) {
    let raf = 0;
    gal.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(gal.scrollLeft / Math.max(1, gal.clientWidth));
        dots.forEach((d, k) => d.classList.toggle('on', k === i));
      });
    }, { passive: true });
  }

  const lb = $('#lightbox');
  if (!lb || typeof lb.showModal !== 'function') return;
  const img = $('[data-lb-img]', lb);
  const count = $('[data-lb-count]', lb);
  const links = () => $$('[data-lb]', gal);
  let idx = 0;
  const show = (i) => {
    const all = links();
    idx = (i + all.length) % all.length;
    const a = all[idx];
    const pic = a.querySelector('img');
    img.src = pic?.dataset.large_image || a.href;
    img.alt = pic?.alt || '';
    count.textContent = all.length > 1 ? `${idx + 1} / ${all.length}` : '';
  };
  gal.addEventListener('click', (e) => {
    const a = e.target.closest('[data-lb]');
    if (!a) return;
    e.preventDefault();
    show(links().indexOf(a));
    lb.showModal();
  });
  lb.addEventListener('click', (e) => {
    const step = e.target.closest('[data-lb-step]');
    if (step) { show(idx + Number(step.dataset.lbStep)); return; }
    if (e.target.closest('[data-lb-close]') || e.target === lb || e.target === img) lb.close();
  });
  lb.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') show(idx + 1);
    if (e.key === 'ArrowLeft') show(idx - 1);
  });
}

// ---------------------------------------------------------------------------
// Size buttons drive WooCommerce's own (hidden) dropdowns.
// ---------------------------------------------------------------------------
function initOptions() {
  const form = $('form.variations_form');
  if (!form) return;
  let variations = [];
  try { variations = JSON.parse(form.dataset.product_variations || '[]') || []; } catch (e) { variations = []; }

  const selects = () => $$('.variations select', form);
  const current = () => Object.fromEntries(selects().map((s) => [s.name, s.value]));

  const refresh = () => {
    const chosen = current();
    $$('[data-opt]', form).forEach((opt) => {
      const name = opt.dataset.opt;
      const sel = $(`select[name="${name}"]`, form);
      const lab = $('[data-opt-current]', opt);
      if (sel && lab) lab.textContent = sel.value ? sel.options[sel.selectedIndex].text : 'Select';
      $$('[data-sizes] button', opt).forEach((b) => {
        const on = sel && sel.value === b.dataset.value;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', String(on));
        if (variations.length) {
          // Available if an in-stock variation matches this value and the other current choices.
          const ok = variations.some((v) => {
            if (!v.is_in_stock || !v.is_purchasable || !v.variation_is_active) return false;
            return Object.entries(v.attributes).every(([k, val]) => {
              if (k === name) return val === '' || val === b.dataset.value;
              return val === '' || !chosen[k] || val === chosen[k];
            });
          });
          b.classList.toggle('is-out', !ok);
          b.setAttribute('aria-label', ok ? b.title : `${b.title} – sold out`);
        }
      });
    });
  };

  $$('[data-sizes]', form).forEach((g) => { g.hidden = false; });

  form.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sizes] button');
    if (!b) return;
    const opt = b.closest('[data-opt]');
    const sel = $(`select[name="${opt.dataset.opt}"]`, form);
    if (!sel) return;
    sel.value = sel.value === b.dataset.value ? '' : b.dataset.value;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    opt.classList.remove('has-err');
    $('[data-opt-err]', opt).textContent = '';
  });

  // Arrow keys move between size buttons (radio group behaviour).
  form.addEventListener('keydown', (e) => {
    const b = e.target.closest('[data-sizes] button');
    if (!b || !['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    const all = $$('button', b.parentElement);
    const next = all[(all.indexOf(b) + (e.key === 'ArrowRight' ? 1 : -1) + all.length) % all.length];
    next.focus();
    e.preventDefault();
  });

  form.addEventListener('change', refresh);
  document.addEventListener('tc:variation', refresh);
  refresh();
}

// ---------------------------------------------------------------------------
// Size guide.
// ---------------------------------------------------------------------------
document.addEventListener('click', (e) => {
  const dlg = $('#size-guide');
  if (!dlg) return;
  if (e.target.closest('[data-sg-open]')) { e.preventDefault(); dlg.showModal(); }
  else if (e.target.closest('[data-sg-close]') || e.target === dlg) dlg.close();
});

// ---------------------------------------------------------------------------
// Add to bag.
// ---------------------------------------------------------------------------
function showOptionError() {
  const form = $('form.variations_form');
  const missing = $$('[data-opt]', form).find((opt) => {
    const sel = $(`select[name="${opt.dataset.opt}"]`, form);
    return sel && !sel.value;
  });
  const target = missing || $$('[data-opt]', form).find((o) => !o.classList.contains('opt--hidden'));
  if (!target) return;
  target.classList.add('has-err');
  const label = ($('.opt__lab span', target)?.textContent || '').split(':')[0].trim().toLowerCase() || 'size';
  const noun = /size/.test(label) ? 'size' : label;
  $('[data-opt-err]', target).textContent = missing ? `Choose a ${noun}.` : 'That one is sold out. Choose another option.';
  target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  $('[data-sizes] button:not(.is-out)', target)?.focus({ preventScroll: true });
}

// Capture phase: runs before WooCommerce's handler, which would show a browser alert.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.single_add_to_cart_button');
  if (!btn) return;
  if (btn.classList.contains('wc-variation-selection-needed') || btn.classList.contains('wc-variation-is-unavailable')) {
    e.preventDefault();
    e.stopImmediatePropagation();
    showOptionError();
  }
}, true);

function applyFragments(fragments) {
  Object.entries(fragments || {}).forEach(([sel, html]) => {
    document.querySelectorAll(sel).forEach((el) => { el.outerHTML = html; });
  });
}

function setStatus(btn, state, text) {
  btn.classList.remove('loading', 'ok');
  if (state) btn.classList.add(state);
  if (text) btn.textContent = text;
}

document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!form.matches('form.cart')) return;
  if (!window.fetch || form.dataset.tcNative) return;
  const btn = e.submitter || $('.single_add_to_cart_button', form);
  if (!btn || btn.classList.contains('disabled')) return;
  e.preventDefault();

  const label = btn.textContent;
  setStatus(btn, 'loading', label);
  btn.setAttribute('aria-busy', 'true');
  const errBox = $('.atc-err', form.parentElement) || Object.assign(document.createElement('p'), { className: 'atc-err', role: 'alert' });
  errBox.textContent = '';
  form.after(errBox);

  try {
    const body = new FormData(form, btn.name ? btn : undefined);
    const res = await fetch(form.action || location.href, { method: 'POST', body, credentials: 'same-origin' });
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const error = doc.querySelector('.woocommerce-error li, .woocommerce-error');
    if (error) {
      setStatus(btn, null, label);
      errBox.textContent = error.textContent.trim().replace(/\s+/g, ' ');
      return;
    }
    const fr = await fetch(endpoint('get_refreshed_fragments'), { method: 'POST', credentials: 'same-origin' }).then((r) => r.json());
    applyFragments(fr.fragments);
    if (window.jQuery) {
      // Lets WooCommerce store the fragments and tracking plugins record the add to cart.
      window.jQuery(document.body).trigger('added_to_cart', [fr.fragments, fr.cart_hash, window.jQuery(btn)]);
    } else {
      document.dispatchEvent(new CustomEvent('tc:cart:added', { detail: fr }));
    }
    setStatus(btn, 'ok', 'Added');
    setTimeout(() => setStatus(btn, null, label), 2000);
  } catch (err) {
    form.dataset.tcNative = '1';
    form.requestSubmit(btn.name ? btn : undefined);
  } finally {
    btn.removeAttribute('aria-busy');
  }
});

// ---------------------------------------------------------------------------
// Reviews: the stars under the title, and links back after posting a review
// (#comment-123, #respond, #reviews), open the Reviews section of the accordion.
// ---------------------------------------------------------------------------
function openReviews(scrollTo) {
  const acc = $('#acc-reviews');
  if (!acc) return;
  acc.open = true;
  const target = (scrollTo && $(scrollTo)) || acc;
  requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
}
document.addEventListener('click', (e) => {
  const a = e.target.closest('a[href="#acc-reviews"]');
  if (!a) return;
  e.preventDefault();
  openReviews();
});
if (/^#(acc-reviews|reviews|respond|comment-\d+)$/.test(location.hash)) openReviews(location.hash);

initGallery();
initOptions();
